// Firma geneli kargo takibi (Stok Yönetimi › Kargo Takip ve Profil › Kargolarım).
//
// Kayıtlar `CompanyShipments` koleksiyonunda tutulur. Vizit kargolarından
// (Visits/{id}.shipments, bkz. utils/shipments.js) bağımsızdır; yalnızca firma,
// durum ve biçimlendirme yardımcıları oradan paylaşılır.
//
// STOK ENTEGRASYONU
// Bir kargoya stok kalemleri bağlanabilir. Kalemler stoğa şu anlarda işlenir:
//   - Gelen kargo  "Teslim Alındı" olunca   → stok ARTAR
//   - Giden kargo  "Kargoya Verildi/Teslim" → stok AZALIR
// Durum geri alınır, kalemler değişir veya kayıt silinirse etki geri çevrilir.
// İşlenen miktarlar kaydın `appliedItems` alanında saklanır; böylece aynı etki
// iki kez uygulanmaz. Hareketler bilerek StockMovements'a YAZILMAZ: oradaki
// tüketim dışı her kayıt raporlarda "çalışana verilen stok" sayılır.

import { collection, doc, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { SHIPMENT_STATUS, parseShipmentCost } from './shipments';

export const COMPANY_SHIPMENTS_COLLECTION = 'CompanyShipments';

// Employees belgesindeki alan: profilden kargo gönderme/alma yetkisi
export const CARGO_ACCESS_FIELD = 'cargoAccess';

export const CARGO_DIRECTION = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
};

export const CARGO_DIRECTION_OPTIONS = [
  {
    value: CARGO_DIRECTION.INCOMING,
    label: 'Gelen Kargo',
    shortLabel: 'Gelen',
    description: 'Firmamıza gönderilen, teslim alınacak kargo',
    partyLabel: 'Gönderen',
    addressLabel: 'Çıkış Adresi',
  },
  {
    value: CARGO_DIRECTION.OUTGOING,
    label: 'Giden Kargo',
    shortLabel: 'Giden',
    description: 'Firmamızdan gönderilen kargo',
    partyLabel: 'Alıcı',
    addressLabel: 'Teslimat Adresi',
  },
];

export const getCargoDirection = (value) =>
  CARGO_DIRECTION_OPTIONS.find((option) => option.value === value) ||
  CARGO_DIRECTION_OPTIONS[1];

// Durum değerleri vizit kargolarıyla ortaktır; etiketler yöne göre değişir.
const STATUS_META = [
  {
    value: SHIPMENT_STATUS.NOT_SENT,
    tone: 'neutral',
    labels: { incoming: 'Bekleniyor', outgoing: 'Hazırlanıyor' },
  },
  {
    value: SHIPMENT_STATUS.SHIPPED,
    tone: 'info',
    labels: { incoming: 'Yolda', outgoing: 'Kargoya Verildi' },
  },
  {
    value: SHIPMENT_STATUS.DELIVERED,
    tone: 'success',
    labels: { incoming: 'Teslim Alındı', outgoing: 'Teslim Edildi' },
  },
  {
    value: SHIPMENT_STATUS.RETURNED,
    tone: 'danger',
    labels: { incoming: 'İade / Sorunlu', outgoing: 'İade / Sorunlu' },
  },
];

export const getCargoStatusOptions = (direction) => {
  const key = getCargoDirection(direction).value;
  return STATUS_META.map((meta) => ({
    value: meta.value,
    tone: meta.tone,
    label: meta.labels[key],
  }));
};

export const getCargoStatus = (direction, status) => {
  const options = getCargoStatusOptions(direction);
  return options.find((option) => option.value === status) || options[0];
};

// Yolda olup beklenen teslim tarihi geçmiş kargo
export const isCargoOverdue = (shipment) => {
  if (!shipment?.expectedAt) return false;
  if (
    shipment.status === SHIPMENT_STATUS.DELIVERED ||
    shipment.status === SHIPMENT_STATUS.RETURNED
  ) {
    return false;
  }
  const expected = new Date(`${shipment.expectedAt}T23:59:59`);
  return !Number.isNaN(expected.getTime()) && expected.getTime() < Date.now();
};

// Bu yön + durumda kalemler stoğa işlenmiş olmalı mı?
export const shouldApplyStock = (direction, status) =>
  direction === CARGO_DIRECTION.INCOMING
    ? status === SHIPMENT_STATUS.DELIVERED
    : status === SHIPMENT_STATUS.SHIPPED || status === SHIPMENT_STATUS.DELIVERED;

export const stockEffectHint = (direction) =>
  direction === CARGO_DIRECTION.INCOMING
    ? 'Kargo "Teslim Alındı" olduğunda bu kalemler stoğa eklenir.'
    : 'Kargo "Kargoya Verildi" olduğunda bu kalemler stoktan düşülür.';

const toQuantity = (value) => {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

// Formdaki kalemleri temizler: stok seçilmemiş / miktarsız satırlar atılır,
// aynı stok birden çok satırdaysa birleştirilir.
export const normalizeCargoItems = (items = []) => {
  const merged = new Map();
  items.forEach((item) => {
    const quantity = toQuantity(item.quantity);
    if (!item.stockId || quantity === 0) return;
    const existing = merged.get(item.stockId);
    merged.set(item.stockId, {
      stockId: item.stockId,
      stockName: item.stockName || existing?.stockName || '',
      quantity: (existing?.quantity || 0) + quantity,
    });
  });
  return [...merged.values()];
};

// Kalemlerin stoğa etkisi (işaretli): gelen +, giden -
const signedItems = (direction, items) =>
  items.map((item) => ({
    stockId: item.stockId,
    stockName: item.stockName,
    quantity: direction === CARGO_DIRECTION.INCOMING ? item.quantity : -item.quantity,
  }));

export class InsufficientStockError extends Error {
  constructor(stockName, available, requested) {
    super(`Yetersiz stok: ${stockName} (kalan ${available}, istenen ${requested})`);
    this.name = 'InsufficientStockError';
  }
}

/**
 * Kargo kaydını ve stok etkisini TEK transaction içinde yazar.
 *
 * @param {string|null} shipmentId  null ise yeni kayıt oluşturulur
 * @param {object|null} data        yazılacak alanlar; null ise kayıt SİLİNİR
 * @returns {Promise<string>} kayıt kimliği
 */
export const saveCompanyShipment = async (shipmentId, data) => {
  const shipmentRef = shipmentId
    ? doc(db, COMPANY_SHIPMENTS_COLLECTION, shipmentId)
    : doc(collection(db, COMPANY_SHIPMENTS_COLLECTION));

  await runTransaction(db, async (transaction) => {
    const snapshot = shipmentId ? await transaction.get(shipmentRef) : null;
    const previous = snapshot?.exists() ? snapshot.data() : null;
    const previouslyApplied = previous?.appliedItems || [];

    const merged = data ? { ...(previous || {}), ...data } : null;
    const nextApplied =
      merged && shouldApplyStock(merged.direction, merged.status)
        ? signedItems(merged.direction, normalizeCargoItems(merged.items))
        : [];

    // Stok başına net değişim: eski etkiyi geri al, yenisini uygula
    const deltas = new Map();
    const names = new Map();
    const addDelta = (item, sign) => {
      deltas.set(item.stockId, (deltas.get(item.stockId) || 0) + sign * item.quantity);
      if (item.stockName) names.set(item.stockId, item.stockName);
    };
    previouslyApplied.forEach((item) => addDelta(item, -1));
    nextApplied.forEach((item) => addDelta(item, 1));

    const changes = [...deltas.entries()].filter(([, delta]) => delta !== 0);
    // Transaction kuralı: tüm okumalar yazmalardan önce yapılır
    const stockSnapshots = await Promise.all(
      changes.map(([stockId]) => transaction.get(doc(db, 'Stocks', stockId)))
    );

    changes.forEach(([stockId, delta], index) => {
      const stockSnapshot = stockSnapshots[index];
      // Silinmiş stok kartı kargo işlemini engellemesin
      if (!stockSnapshot.exists()) return;
      const current = parseFloat(stockSnapshot.data()['Kalan']) || 0;
      const next = current + delta;
      if (next < 0) {
        throw new InsufficientStockError(
          names.get(stockId) || stockSnapshot.data()['Adı'] || 'Stok',
          current,
          Math.abs(delta)
        );
      }
      transaction.update(stockSnapshot.ref, { Kalan: next });
    });

    if (!merged) {
      transaction.delete(shipmentRef);
      return;
    }
    transaction.set(
      shipmentRef,
      { ...data, appliedItems: nextApplied, updatedAt: new Date().toISOString() },
      { merge: true }
    );
  });

  return shipmentRef.id;
};

export const deleteCompanyShipment = (shipmentId) => saveCompanyShipment(shipmentId, null);

// Üst özet kartları
export const summarizeCompanyShipments = (shipments = []) => {
  const summary = { total: shipments.length, incoming: 0, outgoing: 0, inTransit: 0, overdue: 0, totalCost: 0 };
  shipments.forEach((shipment) => {
    if (shipment.direction === CARGO_DIRECTION.INCOMING) summary.incoming += 1;
    else summary.outgoing += 1;
    if (shipment.status === SHIPMENT_STATUS.SHIPPED) summary.inTransit += 1;
    if (isCargoOverdue(shipment)) summary.overdue += 1;
    const cost = parseShipmentCost(shipment.cost);
    if (cost && !Number.isNaN(cost)) summary.totalCost += cost;
  });
  return summary;
};

// Arama kutusu: konu, karşı taraf, takip no, içerik, kayıt sahibi
export const matchesCargoSearch = (shipment, term) => {
  const needle = term.trim().toLocaleLowerCase('tr-TR');
  if (!needle) return true;
  return [
    shipment.subject,
    shipment.partyName,
    shipment.partyOrg,
    shipment.trackingNumber,
    shipment.content,
    shipment.ownerName,
  ].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(needle));
};
